import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { TabId } from '../types';
import { PROJECTS, CONTENT_TABS, ABOUT_GALLERY_IMAGES } from '../data';
import ProjectCard from './ProjectCard';
import StickerBoard from './StickerBoard';
import Reveal from './Reveal';
import styles from '../styles/Tabs.module.css';

const WorkTab: React.FC = () => (
  <div id="work">
    {PROJECTS.map((project, i) => (
      <ProjectCard key={project.id} project={project} index={i} />
    ))}
  </div>
);

const ArtTab: React.FC = () => <StickerBoard />;

const AboutTab: React.FC = () => (
  <div className={styles.aboutPage}>
    <Reveal>
      <h2 className={styles.aboutHello}>hello .ᐟ</h2>
      <p className={styles.aboutIntro}>
        <span className={styles.aboutIntroDesktopOnly}>
          i'm amanda, based in toronto ✈︎
        </span>
        <span className={styles.aboutIntroMobileOnly}>
          i'm amanda. nice to meet you .ᐟ
        </span>
      </p>
    </Reveal>
    <div className={styles.aboutGrid}>
      <Reveal delay={0.06}>
        <div className={styles.aboutRow}>
          <div className={styles.aboutLabel}>the journey</div>
          <div className={styles.aboutBody}>
            i love all things design. between human-computer interaction courses and
            self-driven learning in graphic/web/ux design, i found work that doesn't
            feel like work{' '}
            <span className={styles.aboutBirds} aria-hidden="true">
            𓅰 𓅭 𓅮 𓅯
            </span>
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.12}>
        <div className={styles.aboutRow}>
          <div className={styles.aboutLabel}>where i'm at</div>
          <div className={styles.aboutBody}>
            i recently graduated from queen's university with a bachelor's in computer science! i'm currently working as a graphic designer, with plans to move back into ui/ux/tech.
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.18}>
        <div className={styles.aboutRow}>
          <div className={styles.aboutLabel}>you'll find me</div>
          <div className={styles.aboutBody}>
            in the dance studio with friends, journaling, cafe hopping, playing piano, & making
            mini creative side projects.
          </div>
        </div>
      </Reveal>
    </div>
    <Reveal delay={0.24}>
      <div
        className={styles.aboutGallery}
        role="region"
        aria-label="Photo gallery"
      >
        <div className={styles.aboutGalleryGrid}>
          {ABOUT_GALLERY_IMAGES.map((src) => (
            <img
              key={src}
              src={src}
              alt=""
              className={styles.aboutGalleryImg}
              loading="lazy"
              decoding="async"
            />
          ))}
        </div>
      </div>
    </Reveal>
  </div>
);

interface TabsProps {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
}

const Tabs: React.FC<TabsProps> = ({ activeTab, onTabChange }) => {
  const tabBarRef = useRef<HTMLDivElement>(null);
  const btnRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [glider, setGlider] = useState({ width: 0, x: 0, ready: false });

  const updateGlider = useCallback(() => {
    const bar = tabBarRef.current;
    const btn = btnRefs.current.get(activeTab);
    if (!bar || !btn) return;
    const barRect = bar.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    setGlider({
      width: btnRect.width,
      x: btnRect.left - barRect.left,
      ready: true,
    });
  }, [activeTab]);

  useLayoutEffect(() => {
    updateGlider();
    const bar = tabBarRef.current;
    if (!bar || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => updateGlider());
    ro.observe(bar);
    window.addEventListener('resize', updateGlider);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateGlider);
    };
  }, [updateGlider]);

  const setBtnRef = (id: string) => (el: HTMLButtonElement | null) => {
    if (el) btnRefs.current.set(id, el);
    else btnRefs.current.delete(id);
  };

  return (
    <section className={styles.wrapper} id="about">
      <div className={styles.tabBar} ref={tabBarRef}>
        <div
          className={styles.tabGlider}
          style={{
            width: glider.width,
            transform: `translateX(${glider.x}px)`,
            opacity: glider.ready ? 1 : 0,
          }}
          aria-hidden
        />
        {CONTENT_TABS.map(({ id, label }) => (
          <button
            key={id}
            ref={setBtnRef(id)}
            type="button"
            className={`${styles.tabBtn} ${activeTab === id ? styles.tabActive : ''}`}
            onClick={() => onTabChange(id as TabId)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className={styles.divider} />
      <div hidden={activeTab !== 'work'}>
        <WorkTab />
      </div>
      <div hidden={activeTab !== 'art'}>
        <ArtTab />
      </div>
      <div hidden={activeTab !== 'about'}>
        <AboutTab />
      </div>
    </section>
  );
};

export default Tabs;
